<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { IonContent, IonFooter, IonButton, IonIcon, IonSpinner, IonRefresher, IonRefresherContent, RefresherCustomEvent } from '@ionic/vue';
import { time } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';

import NavigationBar from '@/shared/components/ui/NavigationBar.vue';
import useQueue from '@/modules/queue/composables/useQueue';
import { useAuthStore } from '@/stores/auth/authStore';
import { useAssetsStore } from '@/stores/shared/assetsStore';
import { useCacheImage } from '@/shared/composables/useCacheImage';

// Init
const { t } = useI18n();
const router = useRouter();
const authStore = useAuthStore();
const assetStore = useAssetsStore();
const { loadImage } = useCacheImage();
const logoUrl = ref(assetStore.logoUrl);

// Load logo
if (assetStore.logoUrl) {
	let logoImage = assetStore.logoUrl.split('/').pop();
	logoImage = logoImage ?? 'logo';
	loadImage('logo', logoImage, assetStore.logoUrl).then(url => logoUrl.value = url);
}

// Queue composable
const { getMyQueues, getUnbookedQueueActivities, formatWaitTime } = useQueue();
const { myQueues, isFetchingMyQueues, refetchMyQueues } = getMyQueues();
const { unbookedActivities, isFetchingUnbookedActivities, refetchUnbookedActivities } = getUnbookedQueueActivities();

// Computed queues based on status
const activeQueues = computed(() => {
	return myQueues.value?.filter(slot =>
		['active', 'waiting', 'called', 'in_progress'].includes(slot.status)
	) || [];
});

// Group slots by activity and date, show one entry per activity per date
// Sorted by date (earliest first) and estimated wait time (shortest first)
// Also includes unbooked activities formatted as queue-like items
const uniqueActivityQueues = computed(() => {
	const seen = new Map();
	const result: any[] = [];

	// Add booked slots
	activeQueues.value.forEach(slot => {
		// Get activity ID from slot (try queue_activity.id first, then sub_activity.queue_activity_id)
		const activityId = slot.queue_activity?.id || slot.sub_activity?.queue_activity_id;
		const bookedDate = slot.booked_date;
		const key = `${activityId}_${bookedDate}`;
		if (activityId && !seen.has(key)) {
			seen.set(key, slot);
			result.push(slot);
		}
	});

	// Add unbooked activities formatted as queue-like items
	if (unbookedActivities.value) {
		unbookedActivities.value.forEach(unbooked => {
			const activityId = unbooked.queue_activity_id;
			const key = `unbooked_${activityId}`;
			if (!seen.has(key)) {
				seen.set(key, true);
			// Format as a queue-like item
			const unbookedItem = {
				id: `unbooked_${activityId}`, // Use a unique ID
				queue_activity_id: activityId,
				queue_activity: unbooked.queue_activity,
				is_unbooked: true,
				count: unbooked.count,
				event_ticket_ids: unbooked.event_tickets.map(t => t.id),
				queue_types: unbooked.event_tickets.map(t => t.queue_type || 'normal'),
				status: 'unbooked',
				created_at: new Date().toISOString(), // Use current date for sorting
			};
				result.push(unbookedItem);
			}
		});
	}

	// Sort by unbooked status first (unbooked items go first), then by date (earliest first), then by estimated wait time (shortest first)
	result.sort((a, b) => {
		// Unbooked items go FIRST (before booked items)
		if (a.is_unbooked && !b.is_unbooked) return -1;
		if (!a.is_unbooked && b.is_unbooked) return 1;

		// Get dates for comparison (use booked_date or created_at)
		const dateA = new Date(a.booked_date || a.created_at).getTime();
		const dateB = new Date(b.booked_date || b.created_at).getTime();

		// First, sort by date (earliest first)
		if (dateA !== dateB) {
			return dateA - dateB;
		}

		// If dates are the same, sort by estimated wait time (shortest first)
		const waitTimeA = a.timing?.estimated_wait_minutes || a.estimated_wait_minutes || 0;
		const waitTimeB = b.timing?.estimated_wait_minutes || b.estimated_wait_minutes || 0;
		return waitTimeA - waitTimeB;
	});

	return result;
});

// Computed queue count (total number of slots, not unique activities)
const myQueuesCount = computed(() => activeQueues.value.length);

// Pull to refresh
const handleRefresh = async (event: RefresherCustomEvent) => {
	await refetchMyQueues();
	await refetchUnbookedActivities();
	event.target.complete();
};

// Navigate to ticket detail
const goToTicket = (slotId: number) => {
	router.push(`/queue/ticket/${slotId}`);
};

// Navigate to unbooked activity date selection
const goToUnbookedActivity = (activityId: number, eventTicketIds: number[], queueTypes: string[] = []) => {
	// Store event ticket IDs and queue types in session storage
	sessionStorage.setItem('unbooked_event_ticket_ids', JSON.stringify(eventTicketIds));
	if (queueTypes.length > 0) {
		sessionStorage.setItem('unbooked_queue_types', JSON.stringify(queueTypes));
	}
	router.push(`/queue/${activityId}/date-selection?from_unbooked=true`);
};

// Navigate to activities
const goToActivities = () => {
	router.push('/queue');
};

// Format date for badge
const formatDateBadge = (dateString: string) => {
	const date = new Date(dateString);
	return {
		day: date.getDate(),
		month: date.toLocaleDateString('en-US', { month: 'short' })
	};
};

// Get queue type badge info
const getQueueTypeBadge = (queueType: string) => {
	if (queueType === 'fastlane') {
		return {
			text: t('queue.fastlane'),
			emoji: '⭐️',
		};
	}
	return null;
};

// Format position text
const getPositionText = (slot: any) => {
	const badge = getQueueTypeBadge(slot.queue_type);
	if (badge) {
		return `${t('queue.position')} ${slot.position_number} in the ${badge.emoji} ${badge.text} ${badge.emoji}`;
	}
	return `${t('queue.position')} ${slot.position_number} in the ${t('queue.waiting_line')}`;
};

// Get wait time for banner
const getWaitTimeBanner = (slot: any) => {
	const minutes = slot.timing?.estimated_wait_minutes || slot.estimated_wait_minutes || 0;
	if (minutes < 60) {
		return `${minutes} ${t('queue.minutes')}`;
	}
	const hours = Math.floor(minutes / 60);
	const remainingMins = minutes % 60;
	if (remainingMins === 0) {
		return `${hours}h`;
	}
	return t('queue.hours_minutes_wait', { hours, minutes: remainingMins });
};

// Check if slot is a timeslot-based queue
const isTimeSlotQueue = (slot: any) => {
	const activity = slot.queue_activity || slot.sub_activity?.queue_activity;
	return activity?.queue_type === 'time_slot';
};

// Format timeslot for display (e.g. "19:00 - 19:30")
const formatTimeSlot = (slot: any) => {
	const ts = slot.booked_time_slot;
	if (!ts) return '';

	// If already in range format "HH:MM-HH:MM"
	if (typeof ts === 'string' && ts.includes('-') && !ts.includes('T')) {
		return ts.replace('-', ' - ');
	}

	// Parse ISO or "HH:MM:SS" format
	let startTime: string;
	if (typeof ts === 'string' && ts.includes('T')) {
		const d = new Date(ts);
		startTime = d.getHours().toString().padStart(2, '0') + ':' + d.getMinutes().toString().padStart(2, '0');
	} else {
		startTime = ts.substring(0, 5);
	}

	// Calculate end time from cycle duration if available
	const activity = slot.queue_activity || slot.sub_activity?.queue_activity;
	if (activity?.cycle_duration_minutes) {
		const [h, m] = startTime.split(':').map(Number);
		const endDate = new Date(2000, 0, 1, h, m + activity.cycle_duration_minutes);
		const endTime = endDate.getHours().toString().padStart(2, '0') + ':' + endDate.getMinutes().toString().padStart(2, '0');
		return `${startTime} - ${endTime}`;
	}

	return startTime;
};

// Format timeslot start time only (e.g. "18:27") for the banner
const formatTimeSlotStart = (slot: any) => {
	const ts = slot.booked_time_slot;
	if (!ts) return '';

	if (typeof ts === 'string' && ts.includes('T')) {
		const d = new Date(ts);
		return d.getHours().toString().padStart(2, '0') + ':' + d.getMinutes().toString().padStart(2, '0');
	}

	// "HH:MM:SS" or "HH:MM-HH:MM"
	return ts.substring(0, 5);
};

// Format date as dd.mm.yyyy
const formatDateShort = (dateString: string) => {
	const d = new Date(dateString);
	const day = d.getUTCDate().toString().padStart(2, '0');
	const month = (d.getUTCMonth() + 1).toString().padStart(2, '0');
	const year = d.getUTCFullYear();
	return `${day}.${month}.${year}`;
};

// Check if booked timeslot has passed
const isTimeslotPassed = (slot: any) => {
	// Only check for time_slot queue types
	const activity = slot.queue_activity || slot.sub_activity?.queue_activity;
	const isTimeSlot = activity?.queue_type === 'time_slot' && !!slot.booked_time_slot;

	if (!isTimeSlot) {
		return false; // Not a time slot booking, so not applicable
	}

	const timeslot = slot.booked_time_slot;
	const bookedDate = slot.booked_date;

	if (!timeslot || !bookedDate) {
		return false; // No timeslot info, allow fastpass
	}

	try {
		// First, check if the booked date itself is in the past
		const bookedDateObj = new Date(bookedDate);
		const today = new Date();
		today.setHours(0, 0, 0, 0); // Reset to start of today for comparison
		bookedDateObj.setHours(0, 0, 0, 0); // Reset to start of booked date for comparison

		// If booked date is before today, the timeslot has definitely passed
		if (bookedDateObj < today) {
			return true;
		}

		// If booked date is in the future, the timeslot hasn't passed
		if (bookedDateObj > today) {
			return false;
		}

		// If booked date is today, check if the end time has passed
		let endTime: Date | null = null;

		// Check if timeslot is a UTC datetime string (e.g., "2025-10-31T09:00:00.000000Z")
		const isUtcDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(timeslot.trim());

		if (isUtcDateTime) {
			// Parse UTC datetime
			const utcDate = new Date(timeslot);
			if (!isNaN(utcDate.getTime())) {
				// Calculate end time from cycle duration
				if (activity && activity.cycle_duration_minutes) {
					endTime = new Date(utcDate.getTime() + activity.cycle_duration_minutes * 60000);
				} else {
					endTime = utcDate; // Use start time as end time if no cycle duration
				}
			}
		} else {
			// Parse time string format (e.g., "10:00", "10:00-11:00")
			const allTimeMatches = timeslot.match(/(\d{1,2}:\d{2})(?::\d{2})?/g);

			if (allTimeMatches && allTimeMatches.length >= 2) {
				// Has both start and end times (e.g., "10:00-11:00")
				const endTimeStr = allTimeMatches[allTimeMatches.length - 1].substring(0, 5);
				const [hours, minutes] = endTimeStr.split(':').map(Number);
				const slotDate = new Date(bookedDate);
				slotDate.setHours(hours, minutes, 0, 0);
				endTime = slotDate;
			} else if (allTimeMatches && allTimeMatches.length === 1) {
				// Single time found, calculate end time from cycle duration
				const startTimeStr = allTimeMatches[0].substring(0, 5);
				const [hours, minutes] = startTimeStr.split(':').map(Number);
				const slotDate = new Date(bookedDate);
				slotDate.setHours(hours, minutes, 0, 0);

				if (activity && activity.cycle_duration_minutes) {
					endTime = new Date(slotDate.getTime() + activity.cycle_duration_minutes * 60000);
				} else {
					endTime = slotDate; // Use start time as end time if no cycle duration
				}
			}
		}

		// Check if end time has passed (only if we're checking today's timeslot)
		if (endTime) {
			return endTime < new Date();
		}

		return false; // Couldn't parse timeslot, allow fastpass
	} catch (error) {
		console.error('Error checking if timeslot has passed:', error);
		return false; // On error, allow fastpass to be safe
	}
};
</script>

<template>
	<ion-content class="my-queues-page">
		<ion-refresher slot="fixed" @ionRefresh="handleRefresh($event)">
			<ion-refresher-content></ion-refresher-content>
		</ion-refresher>

		<!-- Fixed Header -->
		<div class="fixed-header">
			<!-- Logo -->
			<div class="flex items-center justify-center pt-16 mb-4">
				<img :src="logoUrl" class="max-w-[180px]" />
			</div>

			<!-- Tabs -->
			<div v-if="authStore.isAuthenticated()" class="flex items-center justify-center mb-6 px-5">
				<ion-button
					class="mr-2 w-[140px] h-[40px] button-solid rounded-full backdrop-blur bg-white/20 hover:bg-white/20"
					shape="round"
					fill="clear"
					@click="goToActivities">
					<span class="text-base2 text-white">{{ t('queue.activities') }}</span>
				</ion-button>
				<ion-button
					class="w-[140px] h-[40px] button-solid rounded-full hover:bg-white"
					shape="round"
					fill="default">
					<span class="text-base2">
						{{ t('queue.my_queues') }}
						<span v-if="myQueuesCount > 0"> ({{ myQueuesCount }})</span>
					</span>
				</ion-button>
			</div>
		</div>

		<!-- Content with padding for fixed header -->
		<div class="content-container">
			<!-- Auth check -->
			<div v-if="!authStore.isAuthenticated()" class="empty-state">
				<p class="empty-text">{{ t('queue.login_to_view_queues') }}</p>
				<ion-button fill="outline" router-link="/auth/login" class="action-button">
					{{ t('auth.login') }}
				</ion-button>
			</div>

			<!-- Loading State -->
			<div v-else-if="isFetchingMyQueues || isFetchingUnbookedActivities" class="loading-state">
				<ion-spinner name="circular"></ion-spinner>
			</div>

			<!-- Queue Cards -->
			<div v-else-if="uniqueActivityQueues.length > 0" class="queue-cards" :class="{ 'single-card': uniqueActivityQueues.length === 1 }">
				<div v-for="slot in uniqueActivityQueues" :key="slot.id" class="queue-card" :class="{ 'unbooked-card': slot.is_unbooked }" @click="slot.is_unbooked ? goToUnbookedActivity(slot.queue_activity_id, slot.event_ticket_ids, slot.queue_types) : goToTicket(slot.id)">
					<!-- Card Image -->
					<div class="card-image-container">
						<img :src="slot.queue_activity?.main_image || slot.queue_activity?.main_image_url || '/assets/placeholder.jpg'" :alt="slot.queue_activity?.name" class="card-image" />

						<!-- Gradient Overlay -->
						<div class="card-image-gradient"></div>

						<!-- Unbooked Badge -->
						<div v-if="slot.is_unbooked" class="unbooked-badge">
							<span class="unbooked-badge-text">{{ slot.count }} {{ slot.count === 1 ? 'ticket' : 'tickets' }} to book</span>
						</div>

						<!-- Time Banner (diagonal) - only for booked non-timeslot slots -->
						<div v-if="!slot.is_unbooked && !isTimeSlotQueue(slot)" class="time-banner">
							<span class="time-banner-text">{{ getWaitTimeBanner(slot) }}</span>
						</div>
						<!-- Timeslot Banner - for timeslot-based slots (show start time only) -->
						<div v-if="!slot.is_unbooked && isTimeSlotQueue(slot)" class="time-banner">
							<span class="time-banner-text">{{ formatTimeSlotStart(slot) }}</span>
						</div>

						<!-- Date Badge - only for booked slots -->
						<div v-if="!slot.is_unbooked" class="date-badge">
							<div class="date-day">{{ formatDateBadge(slot.booked_date || slot.created_at).day }}</div>
							<div class="date-month">{{ formatDateBadge(slot.booked_date || slot.created_at).month }}</div>
						</div>
					</div>

					<!-- Card Content -->
					<div class="card-content">
						<!-- Title -->
						<h3 class="card-title">{{ slot.queue_activity?.name || t('queue.activity') }}</h3>

						<!-- Timeslot Info - for timeslot-based queues -->
						<template v-if="!slot.is_unbooked && isTimeSlotQueue(slot)">
							<div class="info-row">
								<ion-icon :icon="time" class="info-icon"></ion-icon>
								<p class="info-text">
									<span class="info-bold">{{ formatDateShort(slot.booked_date) }}</span>
								</p>
							</div>
							<div class="info-row">
								<ion-icon :icon="time" class="info-icon"></ion-icon>
								<p class="info-text">
									<span class="info-bold">{{ formatTimeSlot(slot) }}</span>
								</p>
							</div>
						</template>

						<!-- Position & Wait Time - for chronological queues -->
						<template v-if="!slot.is_unbooked && !isTimeSlotQueue(slot)">
							<div class="info-row">
								<ion-icon :icon="time" class="info-icon"></ion-icon>
								<p class="info-text">
									<span class="info-bold">{{ getPositionText(slot) }}</span>
								</p>
							</div>
							<div class="info-row">
								<ion-icon :icon="time" class="info-icon"></ion-icon>
								<p class="info-text">
									<span class="info-bold">{{ formatWaitTime(slot.timing?.estimated_wait_minutes || slot.estimated_wait_minutes) }} </span>
									<span class="info-light pl-1"> {{ t('queue.estimated_waiting_time') }}</span>
								</p>
							</div>
						</template>

						<!-- Unbooked Message -->
						<div v-if="slot.is_unbooked" class="info-row">
							<p class="info-text">
								<span class="info-light">{{ t('queue.select_date_time_to_book') }}</span>
							</p>
						</div>

						<!-- Separator -->
						<div class="separator"></div>

						<!-- Description -->
						<p class="card-description" v-html="slot.queue_activity?.short_description || slot.queue_activity?.description?.substring(0, 100) || t('queue.no_description')"></p>

						<!-- Action Buttons -->
						<div class="action-buttons">
							<ion-button v-if="slot.is_unbooked" fill="solid" expand="block" class="book-button" @click.stop="goToUnbookedActivity(slot.queue_activity_id, slot.event_ticket_ids, slot.queue_types)">
								{{ t('queue.book_now') }}
							</ion-button>
							<ion-button v-else fill="outline" expand="block" class="view-spot-button" @click.stop="goToTicket(slot.id)">
								{{ t('queue.view_spot') }}
							</ion-button>
							<ion-button v-if="!slot.is_unbooked && slot.queue_type === 'normal' && slot.queue_activity?.upsell_price && slot.queue_activity?.upsellCurrency && slot.fastlane_available !== false && slot.status !== 'completed' && !isTimeslotPassed(slot)" fill="solid" expand="block" class="fastpass-button" @click.stop="goToTicket(slot.id)">
								{{ t('queue.buy_fastpass') }}
							</ion-button>
						</div>
					</div>
				</div>
			</div>

			<!-- Empty State -->
			<div v-else class="empty-state">
				<p class="empty-text">{{ t('queue.no_active_queues') }}</p>
				<p class="empty-subtext">{{ t('queue.browse_activities_to_join') }}</p>
				<ion-button fill="outline" @click="goToActivities" class="action-button">
					{{ t('queue.browse_activities') }}
				</ion-button>
			</div>
		</div>

		<div class="h-[100px]">&nbsp;</div>
	</ion-content>

	<ion-footer>
		<NavigationBar item-active="queue" />
	</ion-footer>
</template>

<style lang="scss" scoped>
.my-queues-page {
	--background: #0a0a0a;
}

// Fixed Header
.fixed-header {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 100;
	background: #0a0a0a;
	padding-bottom: 16px;
}

// Content Container
.content-container {
	padding-top: 200px; // Space for fixed header with tabs
	padding-bottom: 100px; // Space for bottom navigation
	min-height: 100vh;
}

// Queue Cards
.queue-cards {
	padding: 20px;
	display: flex;
	flex-direction: column;
	gap: 20px;

	&.single-card {
		align-items: center;
		justify-content: center;

		.queue-card {
			max-width: 500px;
		}
	}
}

.queue-card {
	width: 100%;
	border-radius: 20px;
	overflow: hidden;
	cursor: pointer;
	transition: transform 0.2s ease;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
	background: rgba(255, 255, 255, 0.05);

	&:active {
		transform: scale(0.98);
	}

	&.unbooked-card {
		border: 2px solid rgba(255, 193, 7, 0.5);
		background: rgba(255, 193, 7, 0.05);
	}
}

// Card Image
.card-image-container {
	position: relative;
	width: 100%;
	height: 200px;
}

.card-image {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.card-image-gradient {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: linear-gradient(
		to bottom,
		rgba(0, 0, 0, 0) 0%,
		rgba(0, 0, 0, 0.2) 40%,
		rgba(0, 0, 0, 0.9) 100%
	);
}

// Unbooked Badge
.unbooked-badge {
	position: absolute;
	top: 12px;
	right: 12px;
	background: rgba(255, 193, 7, 0.9);
	backdrop-filter: blur(10px);
	border-radius: 12px;
	padding: 6px 12px;
	z-index: 3;
}

.unbooked-badge-text {
	font-family: 'SourceSansPro-SemiBold', sans-serif;
	font-weight: 600;
	font-size: 12px;
	color: #000000;
	white-space: nowrap;
}

// Time Banner (diagonal)
.time-banner {
	position: absolute;
	top: -2px;
	left: -39px;
	width: 121px;
	height: 45px;
	background: #27c840;
	display: flex;
	align-items: center;
	justify-content: center;
	transform: rotate(-45deg);
	transform-origin: center;
	z-index: 2;
}

.time-banner-text {
	font-family: 'SourceSansPro-SemiBold', sans-serif;
	font-weight: 600;
	font-size: 13px;
	color: #ffffff;
	white-space: nowrap;
}

// Date Badge
.date-badge {
	position: absolute;
	top: 0;
	right: 0;
	width: 48px;
	height: 48px;
	background: rgba(255, 255, 255, 0.4);
	backdrop-filter: blur(10px);
	border-bottom-left-radius: 8px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 2px;
	z-index: 2;
}

.date-day {
	font-family: 'SourceSansPro-SemiBold', sans-serif;
	font-weight: 600;
	font-size: 16px;
	color: #ffffff;
	line-height: 1;
}

.date-month {
	font-family: 'SourceSansPro-SemiBold', sans-serif;
	font-weight: 600;
	font-size: 12px;
	color: #ffffff;
	line-height: 1;
	text-transform: uppercase;
}

// Card Content
.card-content {
	padding: 20px;
	background: rgba(255, 255, 255, 0.05);
}

.card-title {
	color: white;
	font-size: 20px;
	font-weight: 700;
	font-family: 'SourceSansPro-Bold', sans-serif;
	margin: 0 0 12px 0;
	line-height: 1.2;
}

.info-row {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 8px;
}

.info-icon {
	font-size: 16px;
	color: rgba(255, 255, 255, 0.6);
	flex-shrink: 0;
}

.info-text {
	font-family: 'SourceSansPro-Regular', sans-serif;
	font-size: 14px;
	line-height: 1.4;
	margin: 0;
	color: rgba(255, 255, 255, 0.8);
}

.info-bold {
	font-weight: 600;
	color: #ffffff;
}

.info-light {
	font-weight: 400;
	color: rgba(255, 255, 255, 0.6);
}

.separator {
	height: 1px;
	background: rgba(255, 255, 255, 0.1);
	margin: 12px 0;
}

.card-description {
	font-family: 'SourceSansPro-Regular', sans-serif;
	font-size: 14px;
	line-height: 1.4;
	color: rgba(255, 255, 255, 0.7);
	margin: 0 0 16px 0;
}

// Action Buttons
.action-buttons {
	display: flex;
	gap: 12px;
}

.view-spot-button {
	flex: 1;
	--border-width: 2px;
	--border-color: rgba(255, 255, 255, 0.3);
	--border-radius: 25px;
	--background: transparent;
	--background-hover: rgba(255, 255, 255, 0.1);
	--color: #ffffff;
	font-family: 'SourceSansPro-SemiBold', sans-serif;
	font-weight: 600;
	font-size: 14px;
	height: 44px;
	text-transform: none;
}

ion-button.fastpass-button {
	flex: 1;
	--background: #27c840;
	--background-hover: #22b238;
	--background-activated: #1da030;
	--border-radius: 25px;
	--color: #ffffff;
	font-family: 'SourceSansPro-SemiBold', sans-serif;
	font-weight: 600;
	font-size: 14px;
	height: 44px;
	text-transform: none;

	&:hover {
		--background-hover: #22b238 !important;
		--background-hover-opacity: 1.0;
	}
}

ion-button.book-button {
	flex: 1;
	--background: #ffc107;
	--background-hover: #ffb300;
	--background-activated: #ffa000;
	--border-radius: 25px;
	--color: #000000;
	font-family: 'SourceSansPro-SemiBold', sans-serif;
	font-weight: 600;
	font-size: 14px;
	height: 44px;
	text-transform: none;

	&:hover {
		--background-hover: #ffb300 !important;
		--background-hover-opacity: 1.0;
	}
}

// Loading State
.loading-state {
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 400px;

	ion-spinner {
		--color: white;
		width: 48px;
		height: 48px;
	}
}

// Empty State
.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 400px;
	padding: 40px;
	text-align: center;

	.empty-text {
		color: white;
		font-size: 20px;
		font-weight: 600;
		font-family: 'SourceSansPro-SemiBold', sans-serif;
		margin: 0 0 8px 0;
	}

	.empty-subtext {
		color: rgba(255, 255, 255, 0.6);
		font-size: 16px;
		font-family: 'SourceSansPro-Regular', sans-serif;
		margin: 0 0 24px 0;
	}

	.action-button {
		--border-width: 2px;
		--border-color: rgba(255, 255, 255, 0.3);
		--border-radius: 25px;
		--background: transparent;
		--color: #ffffff;
		font-family: 'SourceSansPro-SemiBold', sans-serif;
		font-weight: 600;
		font-size: 16px;
		height: 48px;
		min-width: 200px;
	}
}
</style>
